import { sessionHeaders } from "./auth.js";

function trimBase(apiBase) {
  return String(apiBase || "").replace(/\/+$/, "");
}

function apiUrl(apiBase, path) {
  const b = trimBase(apiBase);
  return b ? `${b}${path}` : path;
}

export function meetClientHint() {
  let client = "web";
  try {
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("client") === "beagle-app") {
      client = "beagle-app";
    }
  } catch {
    /* ignore */
  }
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  let platform = "web";
  if (/iPhone|iPad|iPod/i.test(ua)) platform = "ios";
  else if (/Android/i.test(ua)) platform = "android";
  return { client, platform };
}

export function erizoUrl(apiBase, override) {
  if (override) return override;
  return apiUrl(apiBase, "/erizo.js");
}

export async function fetchRooms(apiBase) {
  const res = await fetch(apiUrl(apiBase, "/api/meetings"), {
    credentials: "include",
    headers: sessionHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to list rooms (${res.status})`);
  const data = await res.json();
  const list = Array.isArray(data) ? data : [];
  return list.map((room) => ({
    _id: String(room._id ?? ""),
    name: String(room.name || `Room ${room._id}`),
    participants: Number(room.participants || 0),
    description: String(room.description || ""),
    icon: String(room.icon || ""),
    requireMic: room.requireMic !== false,
    owner: room.owner || null,
    ownerName: room.ownerName || "",
    pin: Number(room.pin) || 0,
    isOwner: !!room.isOwner,
  }));
}

export async function fetchConfig(apiBase) {
  const res = await fetch(apiUrl(apiBase, "/api/config"), {
    credentials: "include",
    headers: sessionHeaders(),
  });
  if (!res.ok) return { canCreate: false, createEnabled: false, signalling: trimBase(apiBase) || "meet" };
  const data = await res.json();
  return { ...data, signalling: data.licode || trimBase(apiBase) || "meet" };
}

export function currentAppBuild(moduleUrl = import.meta.url) {
  try {
    return new URL(moduleUrl, typeof window !== "undefined" ? window.location.href : "http://localhost/")
      .pathname.split("/").pop() || "";
  } catch {
    return "";
  }
}

export async function fetchAppVersion(apiBase) {
  const res = await fetch(apiUrl(apiBase, "/api/version"), {
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!res.ok) return "";
  const data = await res.json().catch(() => ({}));
  const build = String(data?.build || "");
  return /^index-[A-Za-z0-9_-]+\.js$/.test(build) ? build : "";
}

export function appUpdateUrl(href, build, room) {
  const url = new URL(href);
  url.searchParams.set("_meetv", build);
  if (room?.roomId) {
    url.searchParams.set("id", String(room.roomId));
    if (room.roomName) url.searchParams.set("name", String(room.roomName));
    url.searchParams.set("role", room.role || "presenter");
    url.searchParams.set("resume", "1");
    // Nuve join tokens are one-shot. The refreshed client mints a new token
    // through the authenticated Meet API instead of replaying this one.
    url.searchParams.delete("token");
  }
  return url.toString();
}

export async function fetchProfile(apiBase, userid) {
  const id = String(userid || "").trim();
  if (!id) return null;
  const res = await fetch(apiUrl(apiBase, `/api/profile/${encodeURIComponent(id)}`), {
    credentials: "include",
    headers: sessionHeaders(),
  });
  if (res.status === 401) throw new Error("Sign in with Decent required");
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  return data?.ok ? data : null;
}

export async function fetchRecordings(apiBase) {
  const res = await fetch(apiUrl(apiBase, "/api/recordings"), {
    credentials: "include",
    headers: sessionHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error("Sign in with Decent required");
  if (!res.ok) throw new Error(data.error || `Recordings failed (${res.status})`);
  return {
    recordings: Array.isArray(data.recordings) ? data.recordings : [],
    enabled: data.enabled === true,
    bytes: Number(data.bytes) || 0,
    files: Number(data.files) || 0,
  };
}

export function recordingMediaUrl(apiBase, id) {
  return apiUrl(apiBase, `/api/recordings/${encodeURIComponent(id)}/media`);
}

function presenceKey(roomId, kind) {
  return `beagle-meet-${kind}:${roomId}`;
}

export function meetingConnectionId(roomId) {
  const key = presenceKey(roomId, "connection");
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : "";
    if (id) sessionStorage.setItem(key, id);
    return id;
  } catch {
    return "";
  }
}

export function rememberedParticipant(roomId) {
  try {
    return sessionStorage.getItem(presenceKey(roomId, "participant")) || "";
  } catch {
    return "";
  }
}

export function rememberMeetingPresence(roomId, presence) {
  try {
    if (presence?.connectionId) sessionStorage.setItem(presenceKey(roomId, "connection"), presence.connectionId);
    if (presence?.participantSessionId) sessionStorage.setItem(presenceKey(roomId, "participant"), presence.participantSessionId);
  } catch {
    /* ignore */
  }
}

export async function openMeetingSession(apiBase, roomId, extra = {}) {
  const headers = sessionHeaders();
  headers["Content-Type"] = "application/json";
  const res = await fetch(apiUrl(apiBase, `/api/meetings/${encodeURIComponent(roomId)}/session`), {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({
      connectionId: extra.connectionId || meetingConnectionId(roomId),
      previousParticipantSessionId: extra.previousParticipantSessionId || rememberedParticipant(roomId),
      reconnect: !!extra.reconnect,
    }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.meetingSessionId || !data?.participantSessionId) return null;
  rememberMeetingPresence(roomId, data);
  return data;
}

export function closeMeetingSession(apiBase, roomId, presence, reason = "exit") {
  if (!presence?.participantSessionId) return;
  const headers = sessionHeaders();
  headers["Content-Type"] = "application/json";
  fetch(apiUrl(apiBase, `/api/meetings/${encodeURIComponent(roomId)}/session/leave`), {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers,
    body: JSON.stringify({
      participantSessionId: presence.participantSessionId,
      connectionId: presence.connectionId || "",
      reason,
    }),
  }).catch(() => undefined);
}

export function registerRecording(apiBase, roomId, body) {
  const headers = sessionHeaders();
  headers["Content-Type"] = "application/json";
  fetch(apiUrl(apiBase, `/api/meetings/${encodeURIComponent(roomId)}/recordings`), {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers,
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

export function finishRecording(apiBase, recordingId) {
  const headers = sessionHeaders();
  headers["Content-Type"] = "application/json";
  fetch(apiUrl(apiBase, `/api/recordings/${encodeURIComponent(recordingId)}/stop`), {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers,
    body: "{}",
  }).catch(() => undefined);
}

export async function fetchAccount(apiBase) {
  const res = await fetch(apiUrl(apiBase, "/api/account"), {
    credentials: "include",
    headers: sessionHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error("Sign in with Decent required");
  if (!res.ok) throw new Error(data.error || `Account failed (${res.status})`);
  return data;
}

export async function joinRoom(apiBase, roomId, role, extras = {}) {
  const res = await fetch(apiUrl(apiBase, "/api/meetings/join"), {
    method: "POST",
    credentials: "include",
    headers: sessionHeaders(),
    body: JSON.stringify({
      roomId,
      role: role || "presenter",
      reconnect: !!extras.reconnect,
      ...meetClientHint(),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error("Sign in with Decent required");
  if (!res.ok) throw new Error(data.error || `Join failed (${res.status})`);
  if (!data?.token) throw new Error("Join did not return a token");
  return data;
}

export async function createRoom(apiBase, name, extras = {}) {
  const res = await fetch(apiUrl(apiBase, "/api/meetings"), {
    method: "POST",
    credentials: "include",
    headers: sessionHeaders(),
    body: JSON.stringify({
      name,
      requireMic: extras.requireMic !== false,
      description: extras.description || "",
      icon: extras.icon || "",
      engine: extras.engine === "agora" ? "agora" : "licode",
    }),
  });
  const text = await res.text();
  if (res.status === 401) throw new Error("Sign in with Decent required");
  if (!res.ok) {
    try {
      const data = JSON.parse(text);
      throw new Error(data.error || `Create failed (${res.status})`);
    } catch (err) {
      if (err instanceof Error && err.message !== `Create failed (${res.status})`) throw err;
      throw new Error(`Create failed (${res.status})`);
    }
  }
  try {
    return JSON.parse(text);
  } catch {
    return { name };
  }
}

export async function updateRoom(apiBase, roomId, extras = {}) {
  const res = await fetch(apiUrl(apiBase, `/api/meetings/${encodeURIComponent(roomId)}`), {
    method: "PATCH",
    credentials: "include",
    headers: sessionHeaders(),
    body: JSON.stringify({
      description: extras.description ?? "",
      icon: extras.icon ?? "",
      requireMic: extras.requireMic !== false,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error("Sign in with Decent required");
  if (!res.ok) throw new Error(data.error || `Update failed (${res.status})`);
  return data;
}

export async function leaveRoom(apiBase, roomId) {
  const headers = sessionHeaders();
  headers["Content-Type"] = "application/json";
  await fetch(apiUrl(apiBase, "/api/meetings/leave"), {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers,
    body: JSON.stringify({ roomId, ...meetClientHint() }),
  }).catch(() => undefined);
}

export function reportLink(apiBase, roomId, type, reason) {
  const at = Date.now();
  const line = `[beagle-meet] ${new Date(at).toISOString()} ${type} room=${roomId}${reason ? ` reason=${reason}` : ""}`;
  if (type === "drop" || type === "recover-fail") console.warn(line);
  else console.info(line);
  const headers = sessionHeaders();
  headers["Content-Type"] = "application/json";
  fetch(apiUrl(apiBase, "/api/meetings/link"), {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers,
    body: JSON.stringify({
      roomId,
      type,
      reason: reason ? String(reason).slice(0, 300) : "",
      at,
      ...meetClientHint(),
    }),
  }).catch(() => undefined);
  return at;
}

export async function fetchPresence(apiBase, roomId) {
  const res = await fetch(apiUrl(apiBase, `/api/meetings/${encodeURIComponent(roomId)}/presence`), {
    credentials: "include",
    headers: sessionHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (data.unknown || res.status >= 500) return { inRoom: true, unknown: true };
  if (!res.ok) throw new Error(data.error || `Presence failed (${res.status})`);
  return { inRoom: !!data.inRoom };
}

export async function reportMedia(apiBase, roomId, report) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch(apiUrl(apiBase, "/api/meetings/media"), {
      method: "POST", credentials: "include", signal: controller.signal,
      headers: { ...sessionHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ ...report, roomId, ...meetClientHint(),
        build: new URL(import.meta.url).pathname.split("/").pop() }),
    });
    const data = await res.json().catch(() => ({}));
    return Array.isArray(data.repair) ? data.repair.filter((id) => typeof id === "string" && id) : [];
  } catch {
    // Best effort: monitoring must never block a call or trigger recovery.
    return [];
  } finally {
    clearTimeout(timer);
  }
}

export async function blockUser(apiBase, roomId, userid) {
  const res = await fetch(apiUrl(apiBase, `/api/meetings/${encodeURIComponent(roomId)}/block`), {
    method: "POST",
    credentials: "include",
    headers: sessionHeaders(),
    body: JSON.stringify({ userid }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Block failed (${res.status})`);
  return data;
}

export async function fetchHost(apiBase, path) {
  const res = await fetch(apiUrl(apiBase, path), {
    credentials: "include",
    headers: sessionHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error("Sign in with Decent required");
  if (!res.ok) throw new Error(data.error || `Host request failed (${res.status})`);
  return data;
}

export async function postHost(apiBase, path, body) {
  const res = await fetch(apiUrl(apiBase, path), {
    method: "POST",
    credentials: "include",
    headers: sessionHeaders(),
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error("Sign in with Decent required");
  if (!res.ok) throw new Error(data.error || `Host request failed (${res.status})`);
  return data;
}

export function signallingHost(apiBase, fallback) {
  try {
    const raw = trimBase(apiBase) || fallback || "";
    if (!raw) return "meet";
    if (raw.startsWith("http")) return new URL(raw).host;
    return raw;
  } catch {
    return trimBase(apiBase) || "meet";
  }
}
